import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { ClipboardCheck, AlertCircle } from 'lucide-react';
import { AuditDialog } from './AuditDialog';
import { format } from 'date-fns';

type ListingType = 'property' | 'plot' | 'project' | 'commercial';

async function fetchPending(type: ListingType) {
  // Get IDs of listings already audited recently (passed)
  const { data: audited } = await supabase
    .from('listing_audits')
    .select('listing_id, status')
    .eq('listing_type', type)
    .in('status', ['passed', 'rejected']);
  const auditedIds = new Set((audited || []).map(a => a.listing_id));

  const tableMap = {
    property: 'properties',
    plot: 'plots',
    project: 'projects',
    commercial: 'properties',
  } as const;
  const table = tableMap[type];

  let query: any = (supabase as any)
    .from(table)
    .select('id, title, name, city, sub_city, approval_status, status, created_at')
    .order('created_at', { ascending: false })
    .limit(50);

  if (type === 'commercial') {
    query = query.eq('property_type', 'commercial');
  }

  const { data } = await query;
  return (data || []).filter((l: any) => !auditedIds.has(l.id));
}

export function AuditQueue() {
  const [tab, setTab] = useState<ListingType>('property');
  const [auditing, setAuditing] = useState<{ id: string; title: string; type: ListingType } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['audit-queue', tab],
    queryFn: () => fetchPending(tab),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5" /> Pending Audit Queue
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={tab} onValueChange={(v: any) => setTab(v)}>
          <TabsList className="mb-4">
            <TabsTrigger value="property">Properties</TabsTrigger>
            <TabsTrigger value="plot">Plots</TabsTrigger>
            <TabsTrigger value="project">Projects</TabsTrigger>
            <TabsTrigger value="commercial">Commercial</TabsTrigger>
          </TabsList>

          <TabsContent value={tab}>
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-16" />)}
              </div>
            ) : !data?.length ? (
              <div className="py-12 text-center text-muted-foreground">
                <AlertCircle className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p>No listings pending audit</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {data.map((listing: any) => {
                  const title = listing.title?.en || listing.name?.en || 'Untitled';
                  return (
                    <div key={listing.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/30">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm line-clamp-1">{title}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                          <span>{[listing.sub_city, listing.city].filter(Boolean).join(', ')}</span>
                          <span>•</span>
                          <span>{format(new Date(listing.created_at), 'MMM d')}</span>
                          <Badge variant="outline" className="text-[10px] h-4">{listing.approval_status}</Badge>
                        </div>
                      </div>
                      <Button size="sm" onClick={() => setAuditing({ id: listing.id, title, type: tab })}>
                        Audit
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      {auditing && (
        <AuditDialog
          open={!!auditing}
          onOpenChange={(o) => !o && setAuditing(null)}
          listingType={auditing.type}
          listingId={auditing.id}
          listingTitle={auditing.title}
        />
      )}
    </Card>
  );
}
